import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import classnames from 'classnames'
import axios from '~/data/http/axios'
import DropdownMenu from '~/Common-Objects/UI/DropdownMenu'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import UilibBadge from '~/Common-Objects/UI/Badge'
import UilibIcon from '~/Common-Objects/UI/Icon'
import { parseChannelType, canManageInboxSettings } from '~/Classes/conversations'
import { handleGenericError, alterSnackbar, alterSiteDrawer } from '~/data/actions/siteActions'
import Button from '../../components/button'
import * as styles from './index.module.scss'
import AddChannelDialog from './components/addChannelDialog'
import DeleteChannelDialog from './components/deleteChannelDialog'
import RenameChannelDialog from './components/renameChannelDialog'
import Header from '../../components/header'
import i18n from '~/i18n'
import UpgradeDrawerTiers from '~/pages/cp/includes/upgrade/upgradeTiers'
import TierPermissions from '~/Classes/tierPermissions'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'

export default function InboxChannels({ inbox, userRole }) {
    const dispatch = useDispatch()
    const history = useHistory()
    const account = useSelector((state) => state.accountMaster)
    const accountUser = useSelector((state) => state.user)

    const [loading, setLoading] = useState(true)
    const [channels, setChannels] = useState([])
    const [selectedChannel, setSelectedChannel] = useState(null)
    const [isAddChannelDialogOpen, setIsAddChannelDialogOpen] = useState(false)
    const [isRenameChannelDialogOpen, setIsRenameChannelDialogOpen] = useState(false)
    const [isDeleteChannelDialogOpen, setIsDeleteChannelDialogOpen] = useState(false)

    const facebookPermission = TierPermissions('facebookInboxChannel', account).allowed
    const tierAllowed = useTierAllowed('facebookInboxChannel')

    useEffect(() => {
        loadInitialData()
    }, [])

    async function loadInitialData() {
        try {
            await loadChannels()
        } finally {
            setLoading(false)
        }
    }

    async function loadChannels() {
        try {
            const { data } = await axios.get(`/inboxes/${inbox.id}/channel`)
            setChannels(data)
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    async function addChannel(channelType) {
        const urlSuffix = parseChannelType(channelType).toLowerCase().replace(/\s/g, '')

        history.push(`/cp/conversations/${inbox.id}/channels/connect/${urlSuffix}`)
    }

    async function renameChannel(updatedChannel) {
        try {
            await axios.put(`/inboxes/${inbox.id}/channel/${updatedChannel.id}`, updatedChannel)

            loadInitialData()
            setIsRenameChannelDialogOpen(false)
            dispatch(alterSnackbar(true, i18n.t('chat:settings.channels.renamed')))
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    async function deleteChannel(channelToDelete) {
        try {
            await axios.delete(`/inboxes/${inbox.id}/channel/${channelToDelete.id}`)
            loadInitialData()
            setIsDeleteChannelDialogOpen(false)
            dispatch(alterSnackbar(true, i18n.t('chat:settings.channels.deleted')))
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    async function pauseChannel(channel) {
        try {
            await axios.put(`/inboxes/${inbox.id}/channel/${channel.id}`, {
                status: 'paused'
            })
            await loadChannels()
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    async function enableChannel(channel) {
        try {
            await axios.put(`/inboxes/${inbox.id}/channel/${channel.id}`, {
                status: 'running'
            })
            await loadChannels()
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }

    async function upsellFacebook(channel) {
        const res = await axios.get('/accountMaster/plan/next?tierName=' + tierAllowed.requiredTier.name)
        const _drawer = (
            <UpgradeDrawerTiers
                onUpgrade={() => {
                    dispatch(alterSiteDrawer(false))
                    enableChannel(channel)
                }}
                newPlan={res.data}
                upsell={'facebookInboxChannel'}
                close={() => dispatch(alterSiteDrawer(false))}
            />
        )
        dispatch(alterSiteDrawer(true, true, 'right', _drawer, true, 1000, '', null, true))
    }

    if (loading) return <LoadingIcons noClass />

    const emailChannelCount = channels.filter((c) => c.type === 1).length
    const activeEmailChannelCount = channels.filter((c) => c.type === 1 && c.status === 'running').length
    return (
        <div className='col-xs-12 col-sm-12 col-md-8 col-lg-6 inbox_settings_left_content'>
            <Header type='inbox' option='channels' />
            {channels.length > 0 && (
                <ul className={classnames(styles.channelsList, 'faux-list')}>
                    {channels.map((channel) => {
                        const { id, name, type, status } = channel
                        let allowDeactivate = true
                        let allowDelete = true
                        if (type === 1 && emailChannelCount === 1) {
                            allowDeactivate = false
                            allowDelete = false
                        }
                        if (activeEmailChannelCount === 1 && channel.status === 'running' && type === 1) {
                            allowDelete = false
                            allowDeactivate = false
                        }
                        let link = `/cp/conversations/${inbox.id}/channels/${id}`
                        if (channel.status === 'draft' && channel.type === 1)
                            link = `/cp/conversations/${inbox.id}/channels/connect/email?ChannelId=${id}`
                        if (channel.status === 'draft' && channel.type === 2)
                            link = `/cp/conversations/${inbox.id}/channels/connect/webchat?ChannelId=${id}`
                        if (channel.status === 'draft' && channel.type === 3)
                            link = `/cp/conversations/${inbox.id}/channels/connect/facebookmessenger?ChannelId=${id}`

                        if (!canManageInboxSettings(userRole, accountUser.userData.roleId)) {
                            link = ''
                        }

                        const upgradeToFacebook = channel.type === 3 && !facebookPermission

                        return (
                            <li key={id}>
                                {/* Using a div here rather than a Link as we have a nested button */}
                                <div className={styles.teaser}>
                                    <Icon className={styles.teaserIcon} type={type} />
                                    <div>
                                        <Link className={styles.teaserLink} to={link}>
                                            <h2 className={styles.teaserName}>{name}</h2>
                                        </Link>

                                        <span className={styles.teaserType}>{parseChannelType(type)}</span>
                                    </div>

                                    <Badge className={styles.teaserBadge} status={status} />

                                    <DropdownMenu.Root>
                                        <DropdownMenu.Trigger asChild>
                                            <Button aria-label='Additional options' variant='ghost' size='s' iconOnly>
                                                <UilibIcon name='dots-vertical' />
                                            </Button>
                                        </DropdownMenu.Trigger>

                                        <DropdownMenu.Portal>
                                            <DropdownMenu.Content align='end'>
                                                {status === 'running' && allowDeactivate && (
                                                    <DropdownMenu.Item onSelect={() => pauseChannel(channel)}>
                                                        {i18n.t('chat:settings.channels.deActivate')}
                                                    </DropdownMenu.Item>
                                                )}
                                                {status === 'paused' && (
                                                    <DropdownMenu.Item
                                                        onSelect={() =>
                                                            upgradeToFacebook
                                                                ? upsellFacebook(channel)
                                                                : enableChannel(channel)
                                                        }
                                                    >
                                                        {i18n.t(
                                                            upgradeToFacebook
                                                                ? 'chat:settings.channels.upgrade'
                                                                : 'chat:settings.channels.activate'
                                                        )}
                                                    </DropdownMenu.Item>
                                                )}
                                                <DropdownMenu.Item onSelect={() => history.push(link)}>
                                                    {i18n.t('chat:settings.channels.settings')}
                                                </DropdownMenu.Item>
                                                <DropdownMenu.Item
                                                    onSelect={() => {
                                                        // Issue with autofocus when opening dialog from a dropdown https://github.com/radix-ui/primitives/issues/1615
                                                        setSelectedChannel(channel)
                                                        setIsRenameChannelDialogOpen(true)
                                                    }}
                                                >
                                                    {i18n.t('chat:settings.channels.rename')}
                                                </DropdownMenu.Item>
                                                {allowDelete && (
                                                    <>
                                                        <DropdownMenu.Separator />

                                                        <DropdownMenu.Item
                                                            variant='danger'
                                                            onSelect={() => {
                                                                // Issue with autofocus when opening dialog from a dropdown https://github.com/radix-ui/primitives/issues/1615
                                                                setSelectedChannel(channel)
                                                                setIsDeleteChannelDialogOpen(true)
                                                            }}
                                                        >
                                                            {i18n.t('chat:settings.channels.delete')}
                                                        </DropdownMenu.Item>
                                                    </>
                                                )}
                                            </DropdownMenu.Content>
                                        </DropdownMenu.Portal>
                                    </DropdownMenu.Root>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}

            <Button
                className={styles.addButton}
                size='s'
                iconLeft={<UilibIcon name='plus' />}
                onClick={() => setIsAddChannelDialogOpen(true)}
            >
                {i18n.t('chat:settings.channels.addChannel')}
            </Button>

            <AddChannelDialog
                inboxChannels={channels}
                isOpen={isAddChannelDialogOpen}
                onSubmit={addChannel}
                onOpenChange={(isOpen) => setIsAddChannelDialogOpen(isOpen)}
            />

            <RenameChannelDialog
                key={`rename-${selectedChannel?.id}`} // For resetting dialog state when closed/opened
                channel={selectedChannel}
                isOpen={isRenameChannelDialogOpen}
                onSubmit={renameChannel}
                onOpenChange={(isOpen) => setIsRenameChannelDialogOpen(isOpen)}
            />

            <DeleteChannelDialog
                key={`delete-${selectedChannel?.id}`} // For resetting dialog state when closed/opened
                channel={selectedChannel}
                isOpen={isDeleteChannelDialogOpen}
                onSubmit={deleteChannel}
                onOpenChange={(isOpen) => setIsDeleteChannelDialogOpen(isOpen)}
            />
        </div>
    )
}

function Icon({ type, ...rest }) {
    let icon

    switch (type) {
        case 1:
            icon = 'mail-open'
            break
        case 2:
            icon = 'chat'
            break
        case 3:
            icon = 'facebook'
            break
    }

    return <UilibIcon name={icon} {...rest} />
}

function Badge({ status, ...rest }) {
    let color
    let label

    switch (status) {
        case 'draft':
            label = i18n.t('chat:settings.channels.status.draft')
            break

        case 'running':
            color = 'teal'
            label = i18n.t('chat:settings.channels.status.active')
            break

        case 'paused':
            label = i18n.t('chat:settings.channels.status.inactive')
    }

    return (
        <UilibBadge color={color} {...rest}>
            {label}
        </UilibBadge>
    )
}
